// ============================================================================
// MILESTONE HOOKS
//
// Milestones are project-level and persist across sessions, so the query key
// is the project. A story tagged in workshop 1 still resolves to the same
// phase in workshop 3.
// ============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listMilestones, createMilestone, updateMilestone, deleteMilestone,
  assignToMilestone, MilestoneInput,
} from '../services/milestones';
import { Milestone } from '../types/raid';

export const milestonesQueryKey = (projectId?: string) => ['milestones', projectId] as const;

export function useMilestones(projectId?: string) {
  return useQuery<Milestone[]>({
    queryKey: milestonesQueryKey(projectId),
    queryFn: () => listMilestones(projectId!),
    enabled: !!projectId,
    staleTime: 30000,
  });
}

export function useMilestoneMutations(projectId?: string) {
  const qc = useQueryClient();
  const key = milestonesQueryKey(projectId);

  // Assignment counts come back on the list, so a create or a delete changes
  // more than the row itself — refetch rather than patch in place.
  const invalidate = () => qc.invalidateQueries({ queryKey: key });

  const create = useMutation({
    mutationFn: (input: MilestoneInput) => createMilestone(projectId!, input),
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: ({ milestoneId, patch }: { milestoneId: string; patch: Partial<MilestoneInput> }) =>
      updateMilestone(projectId!, milestoneId, patch),
    onSuccess: (updated) => {
      // A rename or a date change touches only this row, so patch it.
      qc.setQueryData<Milestone[]>(key, prev =>
        prev?.map(m => (m.milestoneId === updated.milestoneId ? { ...m, ...updated } : m))
          .sort((a, b) => a.sequence - b.sequence)
      );
    },
  });

  const remove = useMutation({
    mutationFn: ({ milestoneId, force }: { milestoneId: string; force?: boolean }) =>
      deleteMilestone(projectId!, milestoneId, force),
    onSuccess: () => {
      invalidate();
      // Untagging changes stories and features across every session.
      qc.invalidateQueries({ queryKey: ['project-requirements', projectId] });
    },
  });

  const assign = useMutation({
    mutationFn: ({ milestoneId, storyIds, featureIds, sessionId }: {
      milestoneId: string; storyIds?: string[]; featureIds?: string[]; sessionId?: string;
    }) => assignToMilestone(projectId!, milestoneId, { storyIds, featureIds, sessionId }),
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: ['project-requirements', projectId] });
    },
  });

  return { create, update, remove, assign };
}
